import { useQuery } from "@tanstack/react-query";
import axiosInstance from "axiosConfig";
import { getMeasurementPageCount } from "utils/measurementPageCount";
import { getOfflineMeasurementByUuid } from "hooks/useOfflineMeasurements";
import { getOfflineMeasurementClientInfo } from "hooks/useOfflineMeasurementClientInfo";
import { getOfflineMeasurementInstallationInfo } from "hooks/useOfflineMeasurementInstallationInfo";
import { getOfflineMeasurementInfo } from "hooks/useOfflineMeasurementInfo";
import { resolveOfflineUuid } from "hooks/useOfflineUuidMap";

const getMeasurementInfoFromOfflineApiCache = (measurementUuid: string) => {
  try {
    const raw = localStorage.getItem("offlineApiCache");
    if (!raw) return null;

    const cache = JSON.parse(raw) as Record<
      string,
      { data: any; timestamp: number; url: string }
    >;

    const keysToTry = [
      `measurement/current-info/?measurementUuid=${measurementUuid}`,
      `/measurement/current-info/?measurementUuid=${measurementUuid}`,
    ];

    for (const k of keysToTry) {
      const hit = cache?.[k]?.data;
      if (hit) return hit;
    }

    return null;
  } catch (e) {
    console.error("Error reading offlineApiCache for measurement current-info:", e);
    return null;
  }
};

export default function useGetMeasurementInfo(measurementUuid: string | null) {
  return useQuery({
    queryKey: ["currentMeasurementInfo", measurementUuid],
    queryFn: async () => {
      if (!navigator.onLine && measurementUuid) {
        const effectiveUuid = resolveOfflineUuid(measurementUuid);

        // 1) Jeśli pomiar był utworzony ONLINE i wszedł do synchronizacji,
        // to jego /measurement/current-info powinien być w offlineApiCache.
        // Wtedy nie polegamy na lokalnym offlineMeasurements.
        const cachedPayload = getMeasurementInfoFromOfflineApiCache(effectiveUuid);
        if (cachedPayload?.measurement) {
          return cachedPayload;
        }

        // 2) Fallback: pomiar utworzony OFFLINE (trzymany w localStorage offlineMeasurements)
        const offline = getOfflineMeasurementByUuid(effectiveUuid);
        if (!offline) {
          throw new Error("Offline measurement not found");
        }

        const offlineClientInfo = getOfflineMeasurementClientInfo(effectiveUuid);
        const offlineInstallationInfo =
          getOfflineMeasurementInstallationInfo(effectiveUuid);
        const offlineMeasurementInfo = getOfflineMeasurementInfo(effectiveUuid);

        const now = new Date().toISOString();

        return {
          status: "success",
          message: "Current measurement params",
          measurement: {
            id: null,
            uuid: offline.uuid,
            typeOfMeasurementId: offline.typeOfMeasurementId ?? null,
            orderId: offline.orderId ?? null,
            userId: offline.userId ?? null,
            createdAt: offline.createdAt ?? now,
            updatedAt: offline.updatedAt ?? now,
            isCopy: offline.isCopy ?? 0,
            isFinished: offline.isFinished ?? 0,
            measurementInstallationInfo: offlineInstallationInfo
              ? {
                  id: null,
                  uuid: null,
                  theBuildingIsMadeOf: offlineInstallationInfo.theBuildingIsMadeOf,
                  plannedThickness: offlineInstallationInfo.plannedThickness,
                  numberOfAssemblyTeams:
                    offlineInstallationInfo.numberOfAssemblyTeams,
                  plannedAssemblyTime:
                    offlineInstallationInfo.plannedAssemblyTime,
                  measurementId: null,
                  createdAt: null,
                  updatedAt: offlineInstallationInfo.updatedAt,
                  theBuildingIsMadeOfComment:
                    offlineInstallationInfo.theBuildingIsMadeOfComment ?? null,
                  constructionSite: [],
                  installationElements: [],
                  constructionSiteUuids:
                    offlineInstallationInfo.constructionSiteUuids,
                  installationElementsUuids:
                    offlineInstallationInfo.installationElementsUuids,
                }
              : null,
            measurementInfo: offlineMeasurementInfo
              ? {
                  id: null,
                  uuid: null,
                  measurementId: null,
                  roomName: offlineMeasurementInfo.roomName,
                  holeSizeWidth: offlineMeasurementInfo.holeSizeWidth,
                  holeSizeHeight: offlineMeasurementInfo.holeSizeHeight,
                  joinerySizeWidth: offlineMeasurementInfo.joinerySizeWidth,
                  joinerySizeHeight: offlineMeasurementInfo.joinerySizeHeight,
                  windowComment: offlineMeasurementInfo.windowComment ?? null,
                  windowBottomAddition: offlineMeasurementInfo.windowBottomAddition,
                  windowBottomAdditionSize:
                    offlineMeasurementInfo.windowBottomAdditionSize,
                  windowSideAddition: offlineMeasurementInfo.windowSideAddition,
                  windowSideAdditionSize:
                    offlineMeasurementInfo.windowSideAdditionSize,
                  typeOfSlidingWindow:
                    offlineMeasurementInfo.typeOfSlidingWindow ?? null,
                  comment: offlineMeasurementInfo.comment ?? null,
                  createdAt: null,
                  updatedAt: offlineMeasurementInfo.updatedAt,
                  warningComment: offlineMeasurementInfo.warningComment ?? null,
                  windowBottomAdditionComment:
                    offlineMeasurementInfo.windowBottomAdditionComment ?? null,
                  windowSideAdditionComment:
                    offlineMeasurementInfo.windowSideAdditionComment ?? null,
                  windowAddition: [],
                  file: offlineMeasurementInfo.file || [],
                  windowAdditionUuids:
                    offlineMeasurementInfo.windowAdditionUuids || [],
                }
              : null,
            measurementClientInfo: offlineClientInfo
              ? {
                  id: null,
                  uuid: null,
                  measurementId: null,
                  spoutLevel: offlineClientInfo.spoutLevel,
                  typeOfStars: offlineClientInfo.typeOfStars ?? null,
                  thicknessOfCladdingOnStairs: null,
                  kitchenWindowConnectedToTheCountertop:
                    offlineClientInfo.kitchenWindowConnectedToTheCountertop,
                  doesTheClientPlanBlindsShutters:
                    offlineClientInfo.doesTheClientPlanBlindsShutters,
                  comments: offlineClientInfo.comments ?? null,
                  createdAt: null,
                  updatedAt: offlineClientInfo.updatedAt,
                  starsMeasurement: offlineClientInfo.starsMeasurement ?? null,
                  kitchenWindowConnectedToTheCountertopMeasurement:
                    offlineClientInfo.kitchenWindowConnectedToTheCountertopMeasurement ??
                    null,
                  preparationOfOpeningsForTripleLayerInstallation:
                    offlineClientInfo.preparationOfOpeningsForTripleLayerInstallation,
                  changeOfOpeningDimensions:
                    offlineClientInfo.changeOfOpeningDimensions,
                  constructionSiteCleanupInvestor:
                    offlineClientInfo.constructionSiteCleanupInvestor,
                  typeOfBlindInstallation: [],
                  typeOfInstallation: [],
                  roomList: [],
                  typeOfInstallationUuids:
                    offlineClientInfo.typeOfInstallationUuids,
                  typeOfBlindInstallationUuids:
                    offlineClientInfo.typeOfBlindInstallationUuids,
                  roomListUuids: offlineClientInfo.roomListUuids,
                }
              : null,
            typeOfMeasurement: offline.typeOfMeasurement ?? null,
            typeOfMeasurementUuid: null,
            pageCount: getMeasurementPageCount(offline.typeOfMeasurement),
          },
        };
      }

      return axiosInstance
        .get(`/measurement/current-info/?measurementUuid=${measurementUuid}`)
        .then((res) => res.data);
    },
    select: (data: { measurement: any }) => data.measurement,
    retry: false,
    enabled: !!measurementUuid,
  });
}